import { useEffect, useState } from "react";
import axios from "axios";
import { Link, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { API, useAuth } from "@/App";
import LandingPage from "@/pages/LandingPage";
import { SiteLayout, useSite } from "@/website/SiteLayout";
import { SectionList } from "@/website/SectionRenderer";
import { BookingWidget } from "@/website/BookingWidget";

const setMeta = (name, content, attr = "name") => {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content || "");
};

export const applySeo = (page) => {
  const seo = page.seo || {};
  document.title = seo.title || `${page.title} | TheMatheMind`;
  setMeta("description", seo.description);
  setMeta("og:title", seo.title || page.title, "property");
  setMeta("og:description", seo.description, "property");
  if (seo.og_image) setMeta("og:image", seo.og_image.startsWith("/") ? window.location.origin + seo.og_image : seo.og_image, "property");
  let canon = document.querySelector('link[rel="canonical"]');
  if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
  canon.href = `https://www.themathemind.com${page.slug === "home" ? "/" : `/${page.slug}`}`;
};

const NotFound = () => (
  <div className="max-w-xl mx-auto text-center py-28 px-5" data-testid="site-404">
    <p className="text-6xl font-extrabold text-[var(--site-primary)] font-['Manrope']">404</p>
    <h1 className="mt-4 text-2xl font-bold">This page has moved or doesn't exist</h1>
    <Link to="/" className="inline-block mt-6 rounded-full bg-[var(--site-primary)] text-white px-6 py-3 font-semibold">Go to homepage</Link>
  </div>
);

export const PublicPage = () => {
  const { pathname } = useLocation();
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    setState({ loading: true });
    axios.get(`${API}/website/resolve`, { params: { path: pathname } })
      .then((r) => { setState(r.data); if (r.data.page) applySeo(r.data.page); })
      .catch(() => setState({ type: "missing" }));
  }, [pathname]);
  useEffect(() => {
    if (state.page && window.location.hash) setTimeout(() => document.querySelector(window.location.hash)?.scrollIntoView({ behavior: "smooth" }), 300);
    else window.scrollTo(0, 0);
  }, [state.page]);
  if (state.type === "redirect") {
    if (state.to.startsWith("https://")) { window.location.replace(state.to); return null; }
    return <Navigate to={state.to} replace />;
  }
  return (
    <SiteLayout>
      {state.loading ? <div className="py-40 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        : state.page ? <div data-testid={`site-page-${state.page.slug}`}><SectionList sections={state.page.sections} /></div> : <NotFound />}
    </SiteLayout>
  );
};

export const HomeGate = () => {
  const site = useSite();
  if (!site) return <div className="min-h-screen" />;
  return site.cms_home_live ? <PublicPage /> : <LandingPage />;
};

export const BookTrialPage = () => {
  useEffect(() => { applySeo({ slug: "book-trial", title: "Book a Free Trial", seo: { description: "Book a free online trial class for Abacus, Vedic Maths or 11+ with TheMatheMind." } }); }, []);
  return (
    <SiteLayout>
      <section className="bg-[var(--site-accent)]/10 py-14 sm:py-20" data-testid="book-trial-page">
        <div className="max-w-3xl mx-auto px-5">
          <h1 className="text-4xl sm:text-5xl font-extrabold font-['Manrope'] text-center text-slate-900">Book a free trial class</h1>
          <p className="mt-3 text-center text-lg text-slate-600">A friendly 1:1 online session to meet your child, try our methods and recommend the right programme.</p>
          <div className="mt-10"><BookingWidget testId="book-trial" /></div>
        </div>
      </section>
    </SiteLayout>
  );
};

export const SitePreviewPage = () => {
  const { token } = useAuth();
  const id = window.location.pathname.split("/").pop();
  const [page, setPage] = useState(null);
  useEffect(() => {
    if (token) axios.get(`${API}/admin/website/pages/${id}`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setPage(r.data)).catch(() => setPage({ draft_sections: [] }));
  }, [id, token]);
  return <SiteLayout>{page ? <div data-testid="site-preview"><SectionList sections={page.draft_sections || []} /></div> : <div className="py-40" />}</SiteLayout>;
};
